import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { testBackendConnection } from './services/apiConfig';
import { fetchSearchCatalogFromNetwork } from './services/g2TravelApi';
import Home from './pages/Home/Home';
import HotelListing from './pages/HotelListing/HotelListing';
import Hotels from './pages/Hotels/Hotels';
import HotelDetail from './pages/HotelDetail/HotelDetail';
import Booking from './pages/Booking/Booking';
import Checkout from './pages/Checkout/Checkout';
import ThankYou from './pages/ThankYou/ThankYou';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import SignIn from './pages/SignIn/SignIn';
import Register from './pages/Register/Register';
import Profile from './pages/Profile/Profile';
import Payment from './pages/Payment/Payment';
import BookingConfirmation from './pages/BookingConfirmation/BookingConfirmation';
import TestG2Travel from './pages/TestG2Travel/TestG2Travel';
import Admin from './pages/Admin/Admin';
import SearchCatalogResults from './pages/SearchCatalogResults/SearchCatalogResults';
import Duffel from './pages/Duffel/Duffel';
import Flights from './pages/Flights/Flights';
import FlightBooking from './pages/FlightBooking/FlightBooking';
import FlightPayment from './pages/FlightPayment/FlightPayment';
import FlightBookingConfirmation from './pages/FlightBookingConfirmation/FlightBookingConfirmation';
import FlightDetail from './pages/FlightDetail/FlightDetail';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import { buildHotelDetailPath } from './lib/hotelRoutes';
import './App.css';

// Wrapper components to handle navigation and URL params
const HomeWrapper = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSearch = (params) => {
    // Navigate to hotels page with search params in state
    navigate('/hotels', {
      state: {
        searchParams: {
          location: params.location || '',
          checkInDate: params.checkIn || '',
          checkOutDate: params.checkOut || '',
          checkIn: params.checkIn || '', // Keep for backward compatibility
          checkOut: params.checkOut || '', // Keep for backward compatibility
          nights: params.nights || 1,
          adults: params.adults || 1,
          children: params.children || 0,
          guests: params.guests || 1,
          nationality: params.nationality || 'GB',
          currency: params.currency || 'GBP',
          cityId: params.cityId || '',
          propertyId: params.propertyId || '',
          searchTarget: params.searchTarget || 'free'
        }
      }
    });
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  const handleViewHotel = (hotelId, hotelData = null) => {
    console.log('🚀 handleViewHotel called:', { hotelId, hotelData, currentPath: window.location.pathname });
    
    if (!hotelId) {
      console.error('❌ handleViewHotel: hotelId is missing!', { hotelId, hotelData });
      alert('Error: Hotel ID is missing. Please try again.');
      return;
    }
    
    // Preserve search parameters when navigating to hotel detail
    const currentSearch = window.location.search;
    const qs = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;
    const targetPath = buildHotelDetailPath(hotelId, new URLSearchParams(qs));
    
    console.log('🚀 Navigating to:', targetPath, { state: { hotel: hotelData } });
    
    try {
      navigate(targetPath, { state: { hotel: hotelData } });
      window.scrollTo(0, 0);
      console.log('✅ Navigation successful');
    } catch (error) {
      console.error('❌ Navigation error:', error);
      alert('Error navigating to hotel details. Please try again.');
    }
  };

  return (
    <Home 
      onSearch={handleSearch} 
      onNavigate={handleNavigate} 
      onViewHotel={handleViewHotel}
    />
  );
};

const HotelListingWrapper = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const searchParamsObj = {
    location: searchParams.get('location') || '',
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    guests: parseInt(searchParams.get('guests')) || 1,
    cityId: searchParams.get('cityId') || '',
    propertyId: searchParams.get('propertyId') || ''
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  const handleViewHotel = (hotelId, hotelData = null) => {
    console.log('🚀 handleViewHotel called:', { hotelId, hotelData, currentPath: window.location.pathname });
    
    if (!hotelId) {
      console.error('❌ handleViewHotel: hotelId is missing!', { hotelId, hotelData });
      alert('Error: Hotel ID is missing. Please try again.');
      return;
    }
    
    // Preserve search parameters when navigating to hotel detail
    const currentSearch = window.location.search;
    const qs = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;
    const targetPath = buildHotelDetailPath(hotelId, new URLSearchParams(qs));
    
    console.log('🚀 Navigating to:', targetPath, { state: { hotel: hotelData } });
    
    try {
      navigate(targetPath, { state: { hotel: hotelData } });
      window.scrollTo(0, 0);
      console.log('✅ Navigation successful');
    } catch (error) {
      console.error('❌ Navigation error:', error);
      alert('Error navigating to hotel details. Please try again.');
    }
  };

  return (
    <HotelListing 
      onGoHome={handleGoHome} 
      onViewHotel={handleViewHotel} 
      onNavigate={handleNavigate}
      searchParams={searchParamsObj}
    />
  );
};

const HotelDetailWrapper = () => {
  const { id } = useParams();
  // Remount when :id changes so listing preview state never mixes with the previous hotel.
  return <HotelDetail key={id} />;
};

const CheckoutWrapper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const booking = location.state?.booking || null;

  const handleGoBack = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  const handleConfirmBooking = (checkoutData) => {
    console.log('Booking confirmed:', checkoutData);
    navigate('/thank-you', { state: { bookingData: checkoutData } });
    window.scrollTo(0, 0);
  };

  return (
    <Checkout 
      booking={booking}
      onGoBack={handleGoBack}
      onConfirm={handleConfirmBooking}
      onNavigate={handleNavigate}
    />
  );
};

const ThankYouWrapper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bookingData = location.state?.bookingData || null;

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return (
    <ThankYou 
      bookingData={bookingData}
      onGoHome={handleGoHome}
      onNavigate={handleNavigate}
    />
  );
};

const AboutWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <About onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const ContactWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <Contact onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const ProfileWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <Profile onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const TestG2TravelWrapper = () => {
  const navigate = useNavigate();

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <TestG2Travel onNavigate={handleNavigate} />;
};

const SearchCatalogResultsWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <SearchCatalogResults onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const HotelsWrapper = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const searchParamsObj = {
    location: searchParams.get('location') || '',
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    nights: parseInt(searchParams.get('nights')) || 1,
    adults: parseInt(searchParams.get('adults')) || 1,
    children: parseInt(searchParams.get('children')) || 0,
    guests: parseInt(searchParams.get('guests')) || 1,
    nationality: searchParams.get('nationality') || 'GB',
    currency: searchParams.get('currency') || 'GBP',
    cityId: searchParams.get('cityId') || '',
    propertyId: searchParams.get('propertyId') || '',
    searchTarget: searchParams.get('searchTarget') || 'free'
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <Hotels onGoHome={handleGoHome} onNavigate={handleNavigate} searchParams={searchParamsObj} />;
};

const DuffelWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
      duffel: '/duffel',
      flights: '/flights',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <Duffel onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const FlightsWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
      duffel: '/duffel',
      flights: '/flights',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <Flights onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const FlightBookingWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
      duffel: '/duffel',
      flights: '/flights',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <FlightBooking onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const FlightPaymentWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
      duffel: '/duffel',
      flights: '/flights',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <FlightPayment onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

const FlightBookingConfirmationWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
      duffel: '/duffel',
      flights: '/flights',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return (
    <FlightBookingConfirmation onGoHome={handleGoHome} onNavigate={handleNavigate} />
  );
};

const FlightDetailWrapper = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      home: '/',
      listing: '/hotels',
      about: '/about',
      contact: '/contact',
      'test-g2': '/test-g2',
      duffel: '/duffel',
      flights: '/flights',
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return <FlightDetail onGoHome={handleGoHome} onNavigate={handleNavigate} />;
};

function App() {
  // Warm search catalog in background as soon as app opens
  // so destination suggestions feel instant on first interaction.
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      fetchSearchCatalogFromNetwork().catch(() => {});
    };

    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => warm(), { timeout: 1500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(id);
      };
    }

    const t = window.setTimeout(warm, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, []);

  // Test backend connection on app load (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      testBackendConnection().catch(() => {
        console.warn('⚠️ Backend connection test failed. Make sure Edge Function is deployed.');
      });
    }
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="App">
        <Routes>
          <Route path="/" element={<HomeWrapper />} />
          <Route path="/hotels" element={<HotelsWrapper />} />
          <Route path="/hotel/:id" element={<HotelDetailWrapper />} />
          <Route path="/booking/:id" element={<Booking />} />
          <Route path="/checkout" element={<CheckoutWrapper />} />
          <Route path="/thank-you" element={<ThankYouWrapper />} />
          <Route path="/about" element={<AboutWrapper />} />
          <Route path="/contact" element={<ContactWrapper />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<ProfileWrapper />} />
          <Route path="/payment/:id" element={<Payment />} />
          <Route path="/booking-confirmation" element={<BookingConfirmation />} />
          <Route path="/test-g2" element={<TestG2TravelWrapper />} />
          <Route path="/resultssss" element={<SearchCatalogResultsWrapper />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/duffel" element={<DuffelWrapper />} />
          <Route path="/flights" element={<FlightsWrapper />} />
          <Route path="/flights/offer/:offerId" element={<FlightDetailWrapper />} />
          <Route path="/flights/booking-confirmation" element={<FlightBookingConfirmationWrapper />} />
          <Route path="/flights/payment" element={<FlightPaymentWrapper />} />
          <Route path="/flights/book" element={<FlightBookingWrapper />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
